import { useEffect } from 'react';

import { fg } from '@confluence/feature-gating';

import {
	ANALYTICS_OPERATIONAL_ACTION,
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_UI_SUBJECT,
} from '../../constants/snippetAnalytics.ts';
import {
	setOnFetchComplete,
	setOnFetchScopeError,
	type FetchPerformanceMetrics,
	type FetchScopeErrorInfo,
} from '../../store/actions/fetchActions.ts';

import { useSnippetAnalytics } from './useSnippetAnalytics.ts';

/**
 * Registers analytics callbacks for snippet fetch performance metrics and per-scope
 * fetch failures.
 *
 * On successful fetch: fires a FETCHED operational event with performance metrics
 * (snippetCount, userScopeCount, globalScopeCount, payloadSizeBytes, durationMs).
 * Additionally, gated behind `platform_editor_blocks_patch_5`, fires a
 * `snippetApiFetch succeeded` operational event. This success counterpart gives
 * the reliability dashboard a genuine denominator (fetch attempts) so a real fetch
 * error rate can be computed as genuine failures / attempts — consistent with
 * insert and create — instead of borrowing the denominator from rendered fetches.
 *
 * On per-scope failure: fires a snippetApiFetch FAILED operational event with
 * scope and (when available) statusCode attributes, making fetch failures visible
 * in the reliability dashboard (gated behind `platform_editor_blocks_patch_3`).
 * A structured `reason` attribute is added on top, gated behind
 * `platform_editor_blocks_patch_5`, so genuine user-facing fetch failures can be
 * separated from harmless localization/scope no-ops. The raw error message is
 * intentionally omitted to avoid leaking server response bodies (potential
 * PII/UGC) into analytics — only the classified reason enum and statusCode are
 * sent. See the analytics-review guidance.
 *
 * Call this from a component that is mounted before fetchSnippets() runs
 * (e.g. SnippetsProvider).
 */
export const useSnippetsFetchAnalytics = (): void => {
	const { fireOperationalAnalytic, fireFailedAnalytic, fireSuccessAnalytic } =
		useSnippetAnalytics();

	useEffect(() => {
		setOnFetchComplete((metrics: FetchPerformanceMetrics) => {
			fireOperationalAnalytic({
				actionSubject: ANALYTICS_UI_SUBJECT.SNIPPETS,
				action: ANALYTICS_OPERATIONAL_ACTION.FETCHED,
				attributes: metrics,
			});

			// Success counterpart to `snippetApiFetch failed`, so the dashboard can
			// compute a genuine rate (failures / attempts) instead of approximating the
			// denominator with rendered fetches.
			if (fg('platform_editor_blocks_patch_5')) {
				fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_FETCH);
			}
		});

		if (fg('platform_editor_blocks_patch_3')) {
			setOnFetchScopeError((info: FetchScopeErrorInfo) => {
				fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_FETCH, {
					scope: info.scope,
					// The structured failure reason is rolled out independently of the
					// existing scope/statusCode event so it can bake before the dashboard
					// switches to a genuine (reason-filtered) rate.
					...(fg('platform_editor_blocks_patch_5') && { reason: info.reason }),
					...(info.statusCode !== undefined && { statusCode: info.statusCode }),
				});
			});
		}

		return () => {
			setOnFetchComplete(null);
			setOnFetchScopeError(null);
		};
		// `fireOperationalAnalytic`, `fireFailedAnalytic`, and `fireSuccessAnalytic`
		// are memoised in useSnippetAnalytics, so they are referentially stable and
		// safe to list here.
	}, [fireOperationalAnalytic, fireFailedAnalytic, fireSuccessAnalytic]);
};
